import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Grid, Stack, Button, IconButton, TextField, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination,
  Tooltip, Snackbar, InputAdornment, CircularProgress, Drawer, Tab, Tabs,
  Dialog, DialogTitle, DialogContent, DialogActions, Alert,
} from '@mui/material';
import {
  Add, Search, Refresh, PlayCircleOutline, EventRepeat, PauseCircleOutline, CheckCircle,
  WarningAmberRounded, HourglassEmpty, TaskAlt, Bolt, Close, ArrowForward, OpenInNew,
  CalendarMonth, EditCalendar, ThumbUp, ReceiptLong, Schedule, AccountTree,
} from '@mui/icons-material';
import { recurringService, RecurringDashboard, RecurringRow } from '../../services/recurringService';
import { formatCurrency, formatIndianNumber } from '../../utils/currency';
import { useControlRoomColors } from '../../components/common/controlRoom';
import {
  StatCard, TableSurface, headerCellSx, bodyRowSx, TypePill, StatusDot, StatusTone,
  TableEmptyState, ErrorState,
} from '../../components/common/pageKit';

// ---- cadence + status vocab -------------------------------------------------
const FREQUENCIES = ['DAILY', 'WEEKLY', 'LAST_DAY_MONTHLY', 'MONTHLY', 'QUARTERLY', 'HALF_YEARLY', 'YEARLY'];
const FREQ_LABEL: Record<string, string> = {
  DAILY: 'Daily', WEEKLY: 'Weekly', LAST_DAY_MONTHLY: 'Last day / month',
  MONTHLY: 'Monthly', QUARTERLY: 'Quarterly', HALF_YEARLY: 'Half-yearly', YEARLY: 'Yearly',
};
const POSTING_MODES = ['AUTO_POST', 'DRAFT', 'APPROVAL'];
const POSTING_LABEL: Record<string, string> = { AUTO_POST: 'Auto-post', DRAFT: 'Draft', APPROVAL: 'Approval' };
const STATUSES = ['ACTIVE', 'PAUSED', 'ENDED', 'FAILED'];

const STATUS_TONE: Record<string, StatusTone> = {
  ACTIVE: 'success', PAUSED: 'warning', ENDED: 'neutral', FAILED: 'error',
};
const APPROVAL_TONE: Record<string, StatusTone> = {
  NONE: 'neutral', PENDING: 'warning', APPROVED: 'success', REJECTED: 'error',
};
const titleCase = (s?: string | null) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
const modeTint = (m: string, accent: string) => ({ AUTO_POST: '#059669', DRAFT: '#0284c7', APPROVAL: '#d97706' }[m] || accent);

const toNum = (v: any) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const d = (v?: string | null) => (v ? new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const dt = (v?: string | null) => (v ? new Date(v).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');

interface DialogState {
  open: boolean; editId: number | null;
  name: string; templateId: string; frequency: string; startDate: string; endDate: string; postingMode: string;
}
const emptyDialog: DialogState = {
  open: false, editId: null, name: '', templateId: '', frequency: 'MONTHLY',
  startDate: new Date().toISOString().slice(0, 10), endDate: '', postingMode: 'AUTO_POST',
};

const RecurringJournalControlCenter: React.FC = () => {
  const navigate = useNavigate();
  const c = useControlRoomColors();
  const line = `1px solid ${c.line}`;
  const panelSx = { borderRadius: 3, border: line, bgcolor: c.panel } as const;

  const [dash, setDash] = useState<RecurringDashboard | null>(null);
  const [rows, setRows] = useState<RecurringRow[]>([]);
  const [templates, setTemplates] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [runningDue, setRunningDue] = useState(false);

  // filters
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [frequency, setFrequency] = useState('');
  const [postingMode, setPostingMode] = useState('');

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // detail drawer
  const [detailId, setDetailId] = useState<number | null>(null);
  const [detail, setDetail] = useState<any | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [tab, setTab] = useState(0);

  // create/edit dialog
  const [dlg, setDlg] = useState<DialogState>(emptyDialog);
  const [saving, setSaving] = useState(false);

  const loadDash = useCallback(() => { recurringService.dashboard().then(setDash).catch(() => {}); }, []);
  const loadList = useCallback(() => {
    setLoading(true);
    recurringService.list({
      search: search || undefined, status: status || undefined,
      frequency: frequency || undefined, postingMode: postingMode || undefined,
    })
      .then((r) => { setRows(r.recurring || []); setError(null); })
      .catch((e) => setError(e.message || 'Failed to load recurring journals'))
      .finally(() => setLoading(false));
  }, [search, status, frequency, postingMode]);

  useEffect(() => { loadDash(); recurringService.templates().then((r) => setTemplates(r.templates || [])).catch(() => {}); }, [loadDash]);
  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { setPage(0); }, [search, status, frequency, postingMode]);

  const paged = useMemo(() => rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage), [rows, page, rowsPerPage]);
  const filtersActive = !!(search || status || frequency || postingMode);
  const clearFilters = () => { setSearch(''); setStatus(''); setFrequency(''); setPostingMode(''); };

  const refreshAll = () => { loadList(); loadDash(); if (detailId) openDetail(detailId); };

  const rowAction = async (fn: () => Promise<any>, id: number, msg: string) => {
    setBusyId(id);
    try { await fn(); setToast(msg); loadList(); loadDash(); if (detailId === id) openDetail(id); }
    catch (e: any) { setToast(e.message || 'Action failed'); }
    finally { setBusyId(null); }
  };

  const runDue = async () => {
    setRunningDue(true);
    try {
      const r = await recurringService.runDue();
      setToast(`Run complete — ${r.generated} generated, ${r.failed} failed`);
      loadList(); loadDash();
    } catch (e: any) { setToast(e.message || 'Run Due failed'); }
    finally { setRunningDue(false); }
  };

  // ---- detail drawer --------------------------------------------------------
  const openDetail = (id: number) => {
    setDetailId(id); setTab(0); setDetailLoading(true);
    recurringService.detail(id).then((r) => setDetail(r)).catch(() => setDetail(null)).finally(() => setDetailLoading(false));
  };
  const closeDetail = () => { setDetailId(null); setDetail(null); };

  // ---- create / edit dialog -------------------------------------------------
  const openCreate = () => setDlg({ ...emptyDialog, open: true });
  const openEdit = (r: RecurringRow) => setDlg({
    open: true, editId: r.id, name: r.name, templateId: String(r.template_id),
    frequency: r.frequency, startDate: (r.start_date || '').slice(0, 10),
    endDate: (r.end_date || '').slice(0, 10), postingMode: r.posting_mode || 'AUTO_POST',
  });
  const closeDialog = () => setDlg(emptyDialog);
  const saveDialog = async () => {
    setSaving(true);
    try {
      if (dlg.editId) {
        // Edit updates schedule fields (endDate / frequency / posting mode).
        await recurringService.update(dlg.editId, {
          endDate: dlg.endDate || null, frequency: dlg.frequency, postingMode: dlg.postingMode,
        });
        setToast('Schedule updated');
      } else {
        await recurringService.create({
          name: dlg.name, templateId: Number(dlg.templateId), frequency: dlg.frequency,
          startDate: dlg.startDate, endDate: dlg.endDate || null, postingMode: dlg.postingMode,
        });
        setToast('Recurring journal created');
      }
      closeDialog(); loadList(); loadDash();
    } catch (e: any) { setToast(e.message || 'Save failed'); }
    finally { setSaving(false); }
  };
  const dialogValid = dlg.editId
    ? !!dlg.frequency
    : !!(dlg.name && dlg.templateId && dlg.frequency && dlg.startDate);

  // ---- KPI strip ------------------------------------------------------------
  const kpis = dash ? [
    { title: 'Active Schedules', color: '#059669', icon: <EventRepeat />, value: dash.activeTemplates,
      footer: <Typography variant="caption" color="text.secondary">{formatIndianNumber(dash.totalScheduledValue)} scheduled / cycle</Typography> },
    { title: 'Due This Week', color: '#0284c7', icon: <Schedule />, value: dash.dueThisWeek,
      footer: <Typography variant="caption" color="text.secondary">{dash.dueThisMonth} due this month</Typography> },
    { title: 'Posted This Month', color: '#4f46e5', icon: <TaskAlt />, value: formatIndianNumber(dash.postedThisMonth.value),
      footer: <Typography variant="caption" color="text.secondary">{dash.postedThisMonth.count} generated</Typography> },
    { title: 'Pending Approval', color: '#d97706', icon: <HourglassEmpty />, value: dash.pendingApproval,
      onClick: () => setPostingMode('APPROVAL'),
      footer: <Typography variant="caption" color="text.secondary">awaiting sign-off</Typography> },
    { title: 'Failed', color: '#dc2626', icon: <WarningAmberRounded />, value: dash.failed,
      onClick: () => setStatus('FAILED'),
      footer: <Typography variant="caption" color="text.secondary">need attention</Typography> },
    { title: 'Next Posting', color: '#0891b2', icon: <CalendarMonth />, value: dash.nextPostingDate ? d(dash.nextPostingDate) : '—',
      footer: <Typography variant="caption" color="text.secondary">earliest scheduled run</Typography> },
  ] : [];

  const header = detail?.header;

  return (
    <Box sx={{ p: 3, bgcolor: c.bg, minHeight: '100%' }}>
      {/* header */}
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={2} flexWrap="wrap" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>Recurring Journal Control Center</Typography>
          <Typography variant="body2" color="text.secondary">Schedule, monitor and post template-driven journals across every cadence — with a full posting history.</Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Button variant="outlined" startIcon={runningDue ? <CircularProgress size={16} /> : <Bolt />} disabled={runningDue} onClick={runDue}>
            {runningDue ? 'Running…' : 'Run Due'}
          </Button>
          <Button variant="outlined" startIcon={<Refresh />} onClick={refreshAll}>Refresh</Button>
          <Button variant="contained" startIcon={<Add />} onClick={openCreate}>New Recurring Journal</Button>
        </Stack>
      </Stack>

      {/* KPI strip */}
      <Grid container spacing={1.75} sx={{ mb: 2.5 }}>
        {(dash ? kpis : Array.from({ length: 6 })).map((k: any, i) => (
          <Grid size={{ xs: 6, sm: 4, md: 2 }} key={i}>
            {dash
              ? <StatCard title={k.title} value={k.value} icon={k.icon} color={k.color} footer={k.footer} onClick={k.onClick} />
              : <Paper elevation={0} sx={{ ...panelSx, height: 118, display: 'grid', placeItems: 'center' }}><CircularProgress size={20} /></Paper>}
          </Grid>
        ))}
      </Grid>

      {/* filters */}
      <Paper elevation={0} sx={{ ...panelSx, p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField size="small" placeholder="Search name or template…" value={search} onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 240, flexGrow: { xs: 1, md: 0 } }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 18, color: c.sub }} /></InputAdornment> }} />
          <TextField select size="small" label="Status" value={status} onChange={(e) => setStatus(e.target.value)} sx={{ minWidth: 150 }}>
            <MenuItem value="">All statuses</MenuItem>
            {STATUSES.map((s) => <MenuItem key={s} value={s}>{titleCase(s)}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Frequency" value={frequency} onChange={(e) => setFrequency(e.target.value)} sx={{ minWidth: 170 }}>
            <MenuItem value="">All cadences</MenuItem>
            {FREQUENCIES.map((f) => <MenuItem key={f} value={f}>{FREQ_LABEL[f]}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Posting mode" value={postingMode} onChange={(e) => setPostingMode(e.target.value)} sx={{ minWidth: 160 }}>
            <MenuItem value="">All modes</MenuItem>
            {POSTING_MODES.map((m) => <MenuItem key={m} value={m}>{POSTING_LABEL[m]}</MenuItem>)}
          </TextField>
          {filtersActive && <Button size="small" onClick={clearFilters}>Clear</Button>}
          <Box sx={{ flexGrow: 1 }} />
          <Typography variant="body2" color="text.secondary"><b>{rows.length}</b> {rows.length === 1 ? 'schedule' : 'schedules'}</Typography>
        </Box>
      </Paper>

      {error && <Box sx={{ mb: 2 }}><ErrorState message={error} onRetry={loadList} /></Box>}

      {/* table */}
      <TableSurface>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellSx}>Template</TableCell>
                <TableCell sx={headerCellSx}>Name</TableCell>
                <TableCell sx={headerCellSx}>Frequency</TableCell>
                <TableCell sx={headerCellSx}>Next Posting</TableCell>
                <TableCell sx={headerCellSx}>Last Posted</TableCell>
                <TableCell sx={headerCellSx} align="right">Amount</TableCell>
                <TableCell sx={headerCellSx}>Posting Mode</TableCell>
                <TableCell sx={headerCellSx}>Status</TableCell>
                <TableCell sx={headerCellSx}>Approval</TableCell>
                <TableCell sx={headerCellSx} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && rows.length === 0 ? (
                <TableRow><TableCell colSpan={10} align="center" sx={{ border: 0, py: 6 }}><CircularProgress /></TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={10} sx={{ border: 0 }}>
                  <TableEmptyState title="No recurring journals match these filters" hint={filtersActive ? 'Clear the filters to see every schedule.' : 'Create your first recurring journal to automate periodic postings.'} />
                </TableCell></TableRow>
              ) : paged.map((r) => {
                const busy = busyId === r.id;
                const failed = r.status === 'FAILED';
                return (
                  <TableRow key={r.id} hover sx={{ ...(bodyRowSx as any), cursor: 'pointer', ...(failed ? { bgcolor: `${c.bad}0d` } : {}) }}
                    onClick={() => openDetail(r.id)}>
                    <TableCell sx={{ maxWidth: 220 }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Box sx={{ width: 30, height: 30, borderRadius: 1.5, display: 'grid', placeItems: 'center', bgcolor: `${c.accent}1a`, flexShrink: 0 }}>
                          <AccountTree sx={{ fontSize: 16, color: c.accent }} />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.template_name || '—'}</Typography>
                          {r.template_category && <Typography variant="caption" color="text.secondary">{r.template_category}</Typography>}
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell><Typography variant="body2" sx={{ fontWeight: 600 }}>{r.name}</Typography></TableCell>
                    <TableCell><TypePill label={FREQ_LABEL[r.frequency] || r.frequency} tint={c.cyan} /></TableCell>
                    <TableCell><Typography variant="body2">{d(r.next_run_date)}</Typography></TableCell>
                    <TableCell><Typography variant="body2" color="text.secondary">{d(r.last_run_date)}</Typography></TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{formatCurrency(toNum(r.next_amount))}</TableCell>
                    <TableCell><TypePill label={POSTING_LABEL[r.posting_mode] || r.posting_mode} tint={modeTint(r.posting_mode, c.accent)} /></TableCell>
                    <TableCell>
                      <Tooltip title={failed && r.last_error ? r.last_error : ''} disableHoverListener={!failed}>
                        <span><StatusDot label={titleCase(r.status)} tone={STATUS_TONE[r.status] || 'neutral'} /></span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      {r.approval_status && r.approval_status !== 'NONE'
                        ? <StatusDot label={titleCase(r.approval_status)} tone={APPROVAL_TONE[r.approval_status] || 'neutral'} />
                        : <Typography variant="caption" color="text.secondary">—</Typography>}
                    </TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      {busy ? <CircularProgress size={16} /> : (
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Tooltip title="Generate now"><IconButton size="small" onClick={() => rowAction(() => recurringService.generate(r.id), r.id, 'Journal generated')}><PlayCircleOutline sx={{ fontSize: 18, color: c.good }} /></IconButton></Tooltip>
                          {r.approval_status === 'PENDING' &&
                            <Tooltip title="Approve"><IconButton size="small" onClick={() => rowAction(() => recurringService.approve(r.id), r.id, 'Approved')}><ThumbUp sx={{ fontSize: 17, color: c.accent }} /></IconButton></Tooltip>}
                          {r.status === 'ACTIVE'
                            ? <Tooltip title="Pause"><IconButton size="small" onClick={() => rowAction(() => recurringService.pause(r.id), r.id, 'Paused')}><PauseCircleOutline sx={{ fontSize: 18, color: c.warn }} /></IconButton></Tooltip>
                            : <Tooltip title="Resume"><IconButton size="small" onClick={() => rowAction(() => recurringService.resume(r.id), r.id, 'Resumed')}><PlayCircleOutline sx={{ fontSize: 18, color: c.good }} /></IconButton></Tooltip>}
                          <Tooltip title="Edit schedule"><IconButton size="small" onClick={() => openEdit(r)}><EditCalendar sx={{ fontSize: 17 }} /></IconButton></Tooltip>
                          <Tooltip title="Open detail"><IconButton size="small" onClick={() => openDetail(r.id)}><ArrowForward sx={{ fontSize: 17 }} /></IconButton></Tooltip>
                        </Stack>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        {rows.length > 0 && (
          <TablePagination component="div" count={rows.length} page={page} onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage} onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
            rowsPerPageOptions={[10, 25, 50, 100]} />
        )}
      </TableSurface>

      {/* ---- detail drawer ---- */}
      <Drawer anchor="right" open={!!detailId} onClose={closeDetail}
        PaperProps={{ sx: { width: { xs: '100%', sm: 560 }, bgcolor: c.bg } }}>
        {detailLoading || !detail ? (
          <Box sx={{ display: 'grid', placeItems: 'center', height: '100%' }}><CircularProgress /></Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* drawer header */}
            <Box sx={{ p: 2.5, borderBottom: line, position: 'sticky', top: 0, bgcolor: c.panel, zIndex: 2 }}>
              <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>{header.name}</Typography>
                  <Typography variant="body2" color="text.secondary">{header.template_name} · {FREQ_LABEL[header.frequency] || header.frequency}</Typography>
                </Box>
                <IconButton onClick={closeDetail}><Close /></IconButton>
              </Stack>
              <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
                <StatusDot label={titleCase(header.status)} tone={STATUS_TONE[header.status] || 'neutral'} />
                <TypePill label={POSTING_LABEL[header.posting_mode] || header.posting_mode} tint={modeTint(header.posting_mode, c.accent)} />
                {header.approval_status !== 'NONE' && <StatusDot label={`Approval: ${titleCase(header.approval_status)}`} tone={APPROVAL_TONE[header.approval_status] || 'neutral'} />}
              </Stack>
              <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
                <Button size="small" variant="contained" startIcon={<PlayCircleOutline />} onClick={() => rowAction(() => recurringService.generate(header.id), header.id, 'Journal generated')}>Generate now</Button>
                {header.status === 'ACTIVE'
                  ? <Button size="small" variant="outlined" color="warning" startIcon={<PauseCircleOutline />} onClick={() => rowAction(() => recurringService.pause(header.id), header.id, 'Paused')}>Pause</Button>
                  : <Button size="small" variant="outlined" color="success" startIcon={<PlayCircleOutline />} onClick={() => rowAction(() => recurringService.resume(header.id), header.id, 'Resumed')}>Resume</Button>}
                {header.approval_status === 'PENDING' && <Button size="small" variant="outlined" startIcon={<ThumbUp />} onClick={() => rowAction(() => recurringService.approve(header.id), header.id, 'Approved')}>Approve</Button>}
              </Stack>
            </Box>

            <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" sx={{ borderBottom: line, px: 1 }}>
              <Tab label="Overview" /><Tab label="Schedule" /><Tab label="Lines" /><Tab label="Posting History" /><Tab label="Approval" />
            </Tabs>

            <Box sx={{ flex: 1, overflowY: 'auto', p: 2.5 }}>
              {tab === 0 && (
                <Stack spacing={1.5}>
                  {[
                    ['Template', header.template_name], ['Category', header.template_category || '—'],
                    ['Frequency', FREQ_LABEL[header.frequency] || header.frequency],
                    ['Next posting', d(header.next_run_date)], ['Last posted', d(header.last_run_date)],
                    ['Amount / cycle', formatCurrency(toNum(header.next_amount))],
                    ['Generated so far', String(header.generated_count ?? 0)],
                    ['Posting mode', POSTING_LABEL[header.posting_mode] || header.posting_mode],
                  ].map(([k, v]) => (
                    <Stack key={k as string} direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">{k}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{v}</Typography>
                    </Stack>
                  ))}
                  {header.status === 'FAILED' && header.last_error && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>Last run failed {header.last_error_at ? `· ${dt(header.last_error_at)}` : ''}</Typography>
                      {header.last_error}
                    </Alert>
                  )}
                </Stack>
              )}

              {tab === 1 && (
                <Stack spacing={1.5}>
                  {[
                    ['Start date', d(header.start_date)], ['End date', header.end_date ? d(header.end_date) : 'Open-ended'],
                    ['Frequency', FREQ_LABEL[header.frequency] || header.frequency],
                    ['Next run date', d(header.next_run_date)], ['Last run date', d(header.last_run_date)],
                    ['Posting mode', POSTING_LABEL[header.posting_mode] || header.posting_mode],
                  ].map(([k, v]) => (
                    <Stack key={k as string} direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">{k}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{v}</Typography>
                    </Stack>
                  ))}
                  <Button size="small" variant="outlined" startIcon={<EditCalendar />} sx={{ mt: 1, alignSelf: 'flex-start' }}
                    onClick={() => openEdit(header)}>Edit schedule</Button>
                </Stack>
              )}

              {tab === 2 && (
                <TableContainer component={Paper} elevation={0} sx={{ border: line, borderRadius: 2 }}>
                  <Table size="small">
                    <TableHead><TableRow>
                      <TableCell sx={headerCellSx}>Account</TableCell>
                      <TableCell sx={headerCellSx}>Cost Center</TableCell>
                      <TableCell sx={headerCellSx} align="right">Debit</TableCell>
                      <TableCell sx={headerCellSx} align="right">Credit</TableCell>
                    </TableRow></TableHead>
                    <TableBody>
                      {(detail.lines || []).map((l: any) => (
                        <TableRow key={l.id}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{l.account_code} {l.account_name}</Typography>
                            {l.description && <Typography variant="caption" color="text.secondary">{l.description}</Typography>}
                          </TableCell>
                          <TableCell><Typography variant="caption" color="text.secondary">{l.cost_center_name || '—'}</Typography></TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{toNum(l.debit_amount) ? formatCurrency(toNum(l.debit_amount)) : '—'}</TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{toNum(l.credit_amount) ? formatCurrency(toNum(l.credit_amount)) : '—'}</TableCell>
                        </TableRow>
                      ))}
                      {(detail.lines || []).length === 0 && (
                        <TableRow><TableCell colSpan={4} align="center" sx={{ py: 3 }}><Typography variant="caption" color="text.secondary">This template has no lines.</Typography></TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {tab === 3 && (
                <Stack spacing={1}>
                  {(detail.runs || []).length === 0 ? (
                    <Stack alignItems="center" spacing={1} sx={{ py: 5 }}>
                      <ReceiptLong sx={{ fontSize: 30, color: c.sub }} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>No postings yet</Typography>
                      <Typography variant="caption" color="text.secondary">Generate now or wait for the next scheduled run.</Typography>
                    </Stack>
                  ) : (detail.runs || []).map((run: any) => {
                    const tone: StatusTone = run.status === 'POSTED' ? 'success' : run.status === 'FAILED' ? 'error' : 'warning';
                    return (
                      <Paper key={run.id} elevation={0} sx={{ p: 1.5, border: line, borderRadius: 2 }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                          <Box sx={{ minWidth: 0 }}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              {run.entry_number
                                ? <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 700, color: c.accent, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                                    onClick={() => navigate(`/finance/journal/${run.journal_entry_id}`)}>{run.entry_number}<OpenInNew sx={{ fontSize: 13, ml: 0.5, verticalAlign: 'middle' }} /></Typography>
                                : <Typography variant="body2" sx={{ fontWeight: 700, color: c.sub }}>No voucher</Typography>}
                            </Stack>
                            <Typography variant="caption" color="text.secondary">Run {d(run.run_date)}{run.created_at ? ` · logged ${dt(run.created_at)}` : ''}</Typography>
                            {run.status === 'FAILED' && run.error && <Typography variant="caption" sx={{ display: 'block', color: c.bad }}>{run.error}</Typography>}
                          </Box>
                          <Stack alignItems="flex-end" spacing={0.5}>
                            <StatusDot label={titleCase(run.status)} tone={tone} />
                            {toNum(run.je_total) > 0 && <Typography variant="caption" sx={{ fontWeight: 600 }}>{formatCurrency(toNum(run.je_total))}</Typography>}
                          </Stack>
                        </Stack>
                      </Paper>
                    );
                  })}
                </Stack>
              )}

              {tab === 4 && (
                <Stack spacing={1.5}>
                  <Stack direction="row" justifyContent="space-between"><Typography variant="body2" color="text.secondary">Posting mode</Typography><Typography variant="body2" sx={{ fontWeight: 600 }}>{POSTING_LABEL[header.posting_mode] || header.posting_mode}</Typography></Stack>
                  <Stack direction="row" justifyContent="space-between" alignItems="center"><Typography variant="body2" color="text.secondary">Approval status</Typography><StatusDot label={titleCase(header.approval_status)} tone={APPROVAL_TONE[header.approval_status] || 'neutral'} /></Stack>
                  <Stack direction="row" justifyContent="space-between"><Typography variant="body2" color="text.secondary">Approved by</Typography><Typography variant="body2" sx={{ fontWeight: 600 }}>{header.approved_by || '—'}</Typography></Stack>
                  <Stack direction="row" justifyContent="space-between"><Typography variant="body2" color="text.secondary">Approved at</Typography><Typography variant="body2" sx={{ fontWeight: 600 }}>{dt(header.approved_at)}</Typography></Stack>
                  {header.posting_mode === 'APPROVAL' && header.approval_status === 'PENDING' && (
                    <Alert severity="warning" icon={<HourglassEmpty />}>
                      This schedule posts DRAFTs pending approval. Approve it to release automatic posting.
                      <Box sx={{ mt: 1 }}><Button size="small" variant="contained" startIcon={<CheckCircle />} onClick={() => rowAction(() => recurringService.approve(header.id), header.id, 'Approved')}>Approve schedule</Button></Box>
                    </Alert>
                  )}
                  {header.posting_mode !== 'APPROVAL' && (
                    <Typography variant="caption" color="text.secondary">This schedule does not require approval — generated journals are {header.posting_mode === 'AUTO_POST' ? 'posted automatically' : 'saved as drafts'}.</Typography>
                  )}
                </Stack>
              )}
            </Box>
          </Box>
        )}
      </Drawer>

      {/* ---- create / edit dialog ---- */}
      <Dialog open={dlg.open} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{dlg.editId ? 'Edit Recurring Schedule' : 'New Recurring Journal'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <TextField label="Name" value={dlg.name} disabled={!!dlg.editId} onChange={(e) => setDlg({ ...dlg, name: e.target.value })} fullWidth required={!dlg.editId} />
            <TextField select label="Template" value={dlg.templateId} disabled={!!dlg.editId} onChange={(e) => setDlg({ ...dlg, templateId: e.target.value })} fullWidth required={!dlg.editId}
              helperText={dlg.editId ? 'Template cannot be changed after creation' : 'The balanced template these journals are cloned from'}>
              {templates.map((t) => <MenuItem key={t.id} value={String(t.id)}>{t.name}{t.category ? ` · ${t.category}` : ''}</MenuItem>)}
            </TextField>
            <TextField select label="Frequency" value={dlg.frequency} onChange={(e) => setDlg({ ...dlg, frequency: e.target.value })} fullWidth required>
              {FREQUENCIES.map((f) => <MenuItem key={f} value={f}>{FREQ_LABEL[f]}</MenuItem>)}
            </TextField>
            <Stack direction="row" spacing={2}>
              <TextField type="date" label="Start date" InputLabelProps={{ shrink: true }} value={dlg.startDate} disabled={!!dlg.editId}
                onChange={(e) => setDlg({ ...dlg, startDate: e.target.value })} fullWidth required={!dlg.editId} />
              <TextField type="date" label="End date (optional)" InputLabelProps={{ shrink: true }} value={dlg.endDate}
                onChange={(e) => setDlg({ ...dlg, endDate: e.target.value })} fullWidth helperText="Leave blank for open-ended" />
            </Stack>
            <TextField select label="Posting mode" value={dlg.postingMode} onChange={(e) => setDlg({ ...dlg, postingMode: e.target.value })} fullWidth
              helperText="Auto-post posts immediately · Draft saves unposted · Approval requires sign-off">
              {POSTING_MODES.map((m) => <MenuItem key={m} value={m}>{POSTING_LABEL[m]}</MenuItem>)}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button variant="contained" disabled={!dialogValid || saving} onClick={saveDialog}
            startIcon={saving ? <CircularProgress size={16} /> : undefined}>
            {dlg.editId ? 'Save changes' : 'Create schedule'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast(null)} message={toast || ''} />
    </Box>
  );
};

export default RecurringJournalControlCenter;
